import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface EventFilters {
  search?: string;
  category?: string;
  eventType?: string;
  upcoming?: boolean;
  isFree?: boolean;
}

export function useEvents(filters: EventFilters = {}) {
  return useQuery({
    queryKey: ["events", filters],
    queryFn: async () => {
      let query = supabase
        .from("events")
        .select(`
          *,
          event_categories(id, name, slug)
        `)
        .eq("status", "published")
        .eq("is_active", true)
        .order("is_homepage_pinned", { ascending: false })
        .order("is_featured", { ascending: false })
        .order("start_date", { ascending: true });

      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,short_description.ilike.%${filters.search}%`);
      }
      if (filters.category) {
        query = query.eq("category_id", filters.category);
      }
      if (filters.eventType) {
        query = query.eq("event_type", filters.eventType);
      }
      if (filters.upcoming) {
        query = query.gte("start_date", new Date().toISOString());
      }
      if (filters.isFree !== undefined) {
        query = query.eq("is_free", filters.isFree);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useEvent(slug: string) {
  return useQuery({
    queryKey: ["event", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(`
          *,
          event_categories(id, name, slug)
        `)
        .eq("slug", slug)
        .single();

      if (error) throw error;

      // Increment view count
      try {
        await supabase
          .from("events")
          .update({ view_count: (data.view_count || 0) + 1 })
          .eq("id", data.id);
      } catch (e) {
        // Ignore view count errors
      }

      return data;
    },
    enabled: !!slug,
  });
}

export function useEventCategories() {
  return useQuery({
    queryKey: ["event-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_categories")
        .select("*")
        .order("name");

      if (error) throw error;
      return data;
    },
  });
}

export function useUpcomingEvents(limit = 10) {
  return useQuery({
    queryKey: ["upcoming-events", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(`
          *,
          event_categories(id, name, slug)
        `)
        .eq("status", "published")
        .eq("is_active", true)
        .gte("start_date", new Date().toISOString())
        .order("start_date", { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data;
    },
  });
}

export function useEventCalendar(year: number, month: number) {
  return useQuery({
    queryKey: ["event-calendar", year, month],
    queryFn: async () => {
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);

      const { data, error } = await supabase
        .from("events")
        .select(`
          *,
          event_categories(id, name, slug)
        `)
        .eq("status", "published")
        .eq("is_active", true)
        .gte("start_date", startDate.toISOString())
        .lte("start_date", endDate.toISOString())
        .order("start_date", { ascending: true });

      if (error) throw error;
      return data;
    },
  });
}

export function useRegisterForEvent() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ eventId, name, email }: { eventId: string; name: string; email: string }) => {
      const { data, error } = await supabase
        .from("event_registrations")
        .insert({
          event_id: eventId,
          user_id: user?.id || null,
          name,
          email,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error("You are already registered for this event");
        }
        throw error;
      }

      // Update registration count
      try {
        await supabase
          .from("events")
          .update({ registration_count: 1 })
          .eq("id", eventId);
      } catch (e) {
        // Ignore
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-registration"] });
      toast.success("Successfully registered for event!");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to register");
    },
  });
}

export function useAdminEvents() {
  return useQuery({
    queryKey: ["admin-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(`
          *,
          event_categories(id, name, slug)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useEventSubmissions() {
  return useQuery({
    queryKey: ["event-submissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_submissions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateEventStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, status, isActive }: { eventId: string; status?: string; isActive?: boolean }) => {
      const updates: any = { updated_at: new Date().toISOString() };
      if (status !== undefined) updates.status = status;
      if (isActive !== undefined) updates.is_active = isActive;

      const { error } = await supabase
        .from("events")
        .update(updates)
        .eq("id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Event updated");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update event");
    },
  });
}

export function useToggleEventFeatured() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, isFeatured }: { eventId: string; isFeatured: boolean }) => {
      const { error } = await supabase
        .from("events")
        .update({
          is_featured: isFeatured,
          featured_until: isFeatured ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-events"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Featured status updated");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update");
    },
  });
}

export function useReviewEventSubmission() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      submissionId,
      status,
      adminNotes,
    }: {
      submissionId: string;
      status: "approved" | "rejected" | "published";
      adminNotes?: string;
    }) => {
      const { data: submission, error: fetchError } = await supabase
        .from("event_submissions")
        .select("*")
        .eq("id", submissionId)
        .single();

      if (fetchError) throw fetchError;

      // Update submission
      const { error } = await supabase
        .from("event_submissions")
        .update({
          status,
          admin_notes: adminNotes,
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", submissionId);

      if (error) throw error;

      // If approved/published, create the event
      if (status === "approved" || status === "published") {
        const eventData = submission.event_data as any;
        const slug = eventData.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "") + "-" + Date.now();

        const { error: eventError } = await supabase.from("events").insert({
          organizer_id: submission.submitter_id,
          title: eventData.title,
          slug,
          short_description: eventData.short_description,
          description: eventData.description,
          image_url: eventData.image_url,
          category_id: eventData.category_id,
          event_type: eventData.event_type,
          location: eventData.location,
          venue_name: eventData.venue_name,
          start_date: eventData.start_date,
          end_date: eventData.end_date,
          timezone: eventData.timezone,
          registration_url: eventData.registration_url,
          max_attendees: eventData.max_attendees,
          price: eventData.price || 0,
          price_currency: eventData.price_currency || "USD",
          is_free: eventData.is_free ?? true,
          package_type: submission.package_type,
          addons: submission.addons,
          amount_paid: submission.total_amount,
          payment_status: submission.total_amount > 0 ? "paid" : "free",
          status: status === "published" ? "published" : "approved",
          is_active: status === "published",
          is_featured: submission.package_type === "featured" || submission.package_type === "dedicated",
          is_homepage_pinned: submission.addons?.includes("homepage_pin"),
        });

        if (eventError) throw eventError;
      }

      // Send notification
      await supabase.functions.invoke("notify-event-status", {
        body: { submissionId, newStatus: status },
      }).catch(console.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["admin-events"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Submission reviewed");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to review submission");
    },
  });
}
